import { useState } from "react";
import { cn } from "@/lib/utils";
import { SkillsPageSpinnerIcon as SpinnerIcon } from "@/components/icons/SkillsPageSpinnerIcon";
import { SkillsPageFileIcon as FileIcon } from "@/components/icons/SkillsPageFileIcon";
import { SkillsPageFolderIcon as FolderIcon } from "@/components/icons/SkillsPageFolderIcon";
import { SkillsPageChevronIcon as ChevronIcon } from "@/components/icons/SkillsPageChevronIcon";
import { SkillsPageLightbulbIcon as LightbulbIcon } from "@/components/icons/SkillsPageLightbulbIcon";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import type { TreeNode } from "./types";
import type { SkillFileInfo } from "@/hooks/useSkillFiles";

/**
 * Extract YAML frontmatter and body from markdown content.
 * Returns { frontmatter, body } — frontmatter is the raw YAML (without delimiters).
 */
function parseFrontmatter(content: string): {
  frontmatter: string | null;
  body: string;
} {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { frontmatter: null, body: content };
  return { frontmatter: match[1].trim(), body: match[2].trim() };
}

/** Check if a filename is markdown */
function isMarkdownFile(path: string): boolean {
  return /\.(md|mdx|markdown)$/i.test(path);
}

// =============================================================================
// Helpers
// =============================================================================

export function buildFileTree(files: SkillFileInfo[]): TreeNode {
  const root: TreeNode = {
    name: "",
    path: "",
    isDir: true,
    children: new Map(),
  };

  for (const file of files) {
    const parts = file.path.split("/");
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLast = i === parts.length - 1;
      const currentPath = parts.slice(0, i + 1).join("/");

      if (!current.children.has(part)) {
        current.children.set(part, {
          name: part,
          path: currentPath,
          isDir: !isLast,
          children: new Map(),
          file: isLast ? file : undefined,
        });
      }

      current = current.children.get(part)!;
    }
  }

  return root;
}

export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// =============================================================================
// FileTreeItem
// =============================================================================

function FileTreeItem({
  node,
  depth,
  selectedPath,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const isLearnings = node.name === "learnings";
  const sortedChildren = Array.from(node.children.values()).sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  if (node.isDir) {
    return (
      <div>
        <button
          className={cn(
            "w-full flex items-center gap-1.5 py-1 px-1 text-3.5 rounded-8 hover:bg-bg-subtle transition-colors text-left",
            isLearnings && "text-amber-400"
          )}
          style={{ paddingLeft: `${depth * 16 + 4}px` }}
          onClick={() => setExpanded(!expanded)}
        >
          <ChevronIcon
            className="w-3 h-3 text-fg-subtle flex-shrink-0"
            expanded={expanded}
          />
          {isLearnings ? (
            <LightbulbIcon className="w-3.5 h-3.5 flex-shrink-0" />
          ) : (
            <FolderIcon
              className="w-3.5 h-3.5 text-fg-subtle flex-shrink-0"
              open={expanded}
            />
          )}
          <span className="truncate">{node.name}</span>
        </button>
        {expanded &&
          sortedChildren.map((child) => (
            <FileTreeItem
              key={child.path}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
            />
          ))}
      </div>
    );
  }

  const isSelected = selectedPath === node.path;
  return (
    <button
      className={cn(
        "w-full flex items-center gap-1.5 py-1 px-1 text-3.5 rounded-8 transition-colors text-left",
        isSelected
          ? "bg-accent/10 text-accent"
          : "hover:bg-bg-subtle text-fg-muted"
      )}
      style={{ paddingLeft: `${depth * 16 + 4}px` }}
      onClick={() => onSelect(node.path)}
    >
      <FileIcon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="truncate flex-1">{node.name}</span>
      {node.file?.sizeBytes && (
        <span className="text-2.5 text-fg-subtle flex-shrink-0">
          {formatFileSize(node.file.sizeBytes)}
        </span>
      )}
    </button>
  );
}

// =============================================================================
// SkillFileViewer
// =============================================================================

export function SkillFileViewer({
  slug,
  files,
  loading,
  error,
  getFileContent,
  contentCache,
}: {
  slug: string;
  files: SkillFileInfo[];
  loading: boolean;
  error: string | null;
  getFileContent: (slug: string, path: string) => Promise<string | null>;
  contentCache: Map<string, string>;
}) {
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);

  const tree = buildFileTree(files);
  const cacheKey = selectedPath ? `${slug}/${selectedPath}` : null;
  const content = cacheKey ? contentCache.get(cacheKey) : null;
  const isLearningFile = selectedPath?.includes("learnings/");

  const handleSelect = async (path: string) => {
    setSelectedPath(path);
    const key = `${slug}/${path}`;
    if (!contentCache.has(key)) {
      setLoadingContent(true);
      await getFileContent(slug, path);
      setLoadingContent(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <SpinnerIcon className="w-5 h-5 text-fg-muted" />
      </div>
    );
  }

  if (error) {
    return <p className="text-3.5 text-status-error py-4">{error}</p>;
  }

  if (files.length === 0) {
    return (
      <p className="text-3.5 text-fg-muted py-4">
        No files found. This skill uses inline content only.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      <div className="border border-border-subtle rounded-16 p-2 max-h-50 overflow-y-auto">
        {Array.from(tree.children.values())
          .sort((a, b) => {
            if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
            return a.name.localeCompare(b.name);
          })
          .map((child) => (
            <FileTreeItem
              key={child.path}
              node={child}
              depth={0}
              selectedPath={selectedPath}
              onSelect={handleSelect}
            />
          ))}
      </div>

      {selectedPath && (
        <div className="flex-1 min-h-0 flex flex-col border border-border-subtle rounded-16 overflow-hidden">
          <div
            className={cn(
              "flex items-center gap-2 px-3 py-2 border-b border-border-subtle",
              isLearningFile ? "bg-amber-500/5" : "bg-bg-subtle"
            )}
          >
            {isLearningFile ? (
              <LightbulbIcon className="w-3.5 h-3.5 text-amber-400" />
            ) : (
              <FileIcon className="w-3.5 h-3.5 text-fg-subtle" />
            )}
            <span className="text-3 text-fg-muted font-mono text-4 truncate">
              {selectedPath}
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-3">
            {loadingContent ? (
              <div className="flex items-center justify-center py-4">
                <SpinnerIcon className="w-4 h-4 text-fg-muted" />
              </div>
            ) : content !== undefined && content !== null ? (
              selectedPath && isMarkdownFile(selectedPath) ? (
                (() => {
                  const { frontmatter, body } = parseFrontmatter(content);
                  return (
                    <div className="text-3.5 text-fg-default prose-compact">
                      {frontmatter && (
                        <div className="mb-3 rounded-12 border border-border-subtle bg-bg-subtle overflow-hidden">
                          <div className="px-3 py-1.5 border-b border-border-subtle">
                            <span className="text-2.5 font-medium uppercase tracking-wider text-fg-subtle">
                              Frontmatter
                            </span>
                          </div>
                          <pre className="px-3 py-2 text-3 font-mono text-4 text-fg-muted whitespace-pre-wrap m-0 leading-relaxed">
                            {frontmatter}
                          </pre>
                        </div>
                      )}
                      <MarkdownRenderer content={body} />
                    </div>
                  );
                })()
              ) : (
                <pre className="text-3 text-fg-default font-mono text-4 whitespace-pre-wrap break-words leading-relaxed m-0">
                  {content}
                </pre>
              )
            ) : (
              <p className="text-3 text-fg-muted">
                Unable to load file content.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
